import * as React from "react";

import {Layout} from "src/components/organisms/Layout";
import {Promo} from "src/components/organisms/Promo";
import {FAQ} from "src/components/organisms/FAQ";
import {BlockHeading} from "src/components/organisms/BlockHeading";

import schema from "src/images/pages/alpaquita-containers/schema.svg";

import java from "src/images/pages/alpaquita-containers/java.svg";
import c from "src/images/pages/alpaquita-containers/c.svg";
import cPlusPlus from "src/images/pages/alpaquita-containers/c-plus-plus.svg";
import python from "src/images/pages/alpaquita-containers/python.svg";
import liberica from "src/images/common/icon-jdk-labeled-b.svg";
import nik from "src/images/common/icon-nik-labeled-b.svg";

import {graphql, type HeadFC} from "gatsby";
import type {GatsbyTypes} from "gatsby-types";
import Img from "gatsby-image";
import {gatsbyImagePlaceholderStyle} from "util/commons";
import {DhData, EcrData, GcrData, GhcrData, McrData} from "data/alpaquita-containers/faq/data";
import {ContactsForm} from "src/components/organisms/ContactsForm";
import {formItems} from "data/common/formsData";
import * as formStyles from 'src/data/common/forms.module.scss';
import {CodeSnippet} from "src/components/organisms/CodeSnippet";
import {ListBlock} from "src/components/organisms/ListBlock";
import cls from "classnames";
import * as styles from "src/components/organisms/Advantages/styles.module.scss";
import * as pageStyles from "src/styles/pages/alpaquita-containers/alpaquita-containers.module.scss";
import _uniqueId from "lodash/uniqueId";
import {BackgroundImage} from "src/components/organisms/BackgroundImage";
import {StaticImage} from "gatsby-plugin-image";
import {PromoDescription} from "src/components/organisms/Promo/PromoDescription";
import {IdHeading} from "components/organisms/IdHeading";
import {getLtsJdkVersionsString, getLtsJdkVersionsWithLatestString} from "util/jdkVersions";
import {useEffect} from "react";
import {appendLeadInfoScript} from "util/ga/gaHelpers";
import {SmartLink} from "components/atoms/SmartLink";

interface Props {
    data: GatsbyTypes.Query
}

const accordionId1 = _uniqueId("accordion-")
const accordionId2 = _uniqueId("accordion-")
const accordionId3 = _uniqueId("accordion-")

const IndexPage: React.FC<Props> = ({data}) => {

    const promoBackground = (className: string) => {
        const sources = [
            {
                ...data.promoDesktopImage.childImageSharp.fluid,
                media: `(min-width: 1024px)`,
            },
            {
                ...data.promoTabletImage.childImageSharp.fluid,
                media: `(min-width: 768px)`,
            },
            data.promoMobileImage.childImageSharp.fluid,
        ]

        return (
            <Img className={className} fluid={sources} placeholderStyle={gatsbyImagePlaceholderStyle}/>
        )
    }
    return (
        <Layout
            button={{
                className: 'bg-green color-dark'
            }}
            forms={data.forms.nodes}
            popupType="alpaquita"
        >

            <Promo
                className="bg-black"
                style={{maxWidth: '720px'}}
                childrenClassNames={
                    {
                        content: pageStyles.promo,
                        title: pageStyles.title
                    }
                }
                backgroundElement={promoBackground}
                title="Alpaquita Containers"
                subtitle="On Docker Hub, GHCR, GCM, MCR, GCR and Amazon ECR"
                description={`We provide ready container images based on Alpaquita Linux for Java, Python, Go, C/C++, Java native images. Read on to find out how to get them.`}
                buttons={[
                    {
                        caption: 'Docker Hub images',
                        href: '#docker-hub-images',
                        className: 'bg-cyan color-dark'
                    },
                    {
                        caption: 'GHCR images',
                        href: '#ghcr-images',
                        className: 'bg-white color-dark'
                    },
                    {
                        caption: 'MCR images',
                        href: '#mcr-images',
                        className: 'bg-white color-dark'
                    },
                    {
                        caption: 'GCM images',
                        href: '#gcm-images',
                        className: 'bg-white color-dark'
                    },
                    {
                        caption: 'GCR images',
                        href: '#gcr-images',
                        className: 'bg-white color-dark'
                    },
                    {
                        caption: 'Amazon ECR images',
                        href: '#ecr-images',
                        className: 'bg-white color-dark'
                    },
                    {
                        caption: 'Learn more',
                        href: '/blog/bellsoft-s-docker-hub-images-overview/',
                        className: 'bg-white color-dark'
                    }
                ]}
            />

            <BlockHeading text={"Why use Alpaquita containers?"} headingAlign={"left"}
                          containerSize={"normal"}
            >
                <div className={"bs-flex-container"}>
                    <p className={"p-2 color-dark " + pageStyles.whyParagraph}>
                        Alpaquita containers utilize Alpaquita Linux to create a perfect runtime environment for your
                        cloud applications
                    </p>
                </div>
                <ListBlock
                    mainBody={
                        <>
                            <ul className={cls("list-dotted")}>
                                <li className={"p-2 color-gray"}>Zero migration effort with no refactoring
                                </li>
                                <li className={"p-2 color-gray"}>
                                    Minimized server workload with Linux OS fine-tuned for cloud applications
                                </li>
                                <li className={"p-2 color-gray"}>
                                    Faster startup time and lesser memory footprint provide a cost reduction of cloud
                                    expenses
                                </li>
                            </ul>
                        </>
                    }

                    className={cls(styles.listBlock1, "bg-light-gray", pageStyles.listBlock)}
                    childrenClassNames={{}}
                    body={(
                        <>
                            <ul className={cls("list-dotted")}>
                                <li className={"p-2 color-gray"}>
                                    Better security with kernel hardening, process isolation, and other enhancements
                                </li>
                                <li className={"p-2 color-gray"}>
                                    Compatibility with both glibc and musl, including the performant version of musl
                                </li>
                                <li className={"p-2 color-gray"}>
                                    Commercial long term support by the developers of OpenJDK
                                </li>
                                <li className={"p-2 color-gray"}>
                                    CRaC and native image support
                                </li>
                            </ul>
                        </>
                    )}
                />
            </BlockHeading>

            <BlockHeading text={"Supported programming languages and platforms"} headingAlign={"left"} headingLevel={3}
                          containerSize={"normal"}
                          headingClassName={pageStyles.listBlockHeading}
                          className={cls("bg-light-gray", pageStyles.listBlock2)}
            >
                <div className={"bs-flex-container"}>
                    <div className={pageStyles.logoLine}>
                        <div className={pageStyles.logoHeadingWrapper}>
                            <img className={pageStyles.img} src={java} alt={"Java"}/>
                        </div>

                        <div className={pageStyles.verticalLine}></div>

                        <div className={pageStyles.logoHeadingWrapper}>
                            <img className={pageStyles.img} src={python} alt={"C"}/>
                        </div>

                        <div className={pageStyles.verticalLine}></div>

                        <div className={pageStyles.logoHeadingWrapper}>
                            <img className={pageStyles.img} src={c} alt={"C"}/>
                        </div>

                        <div className={pageStyles.verticalLine}></div>

                        <div className={pageStyles.logoHeadingWrapper}>
                            <img className={pageStyles.img} src={cPlusPlus} alt={"C++"}/>
                        </div>

                        <div className={pageStyles.verticalLine}></div>

                        <div className={pageStyles.logoHeadingWrapper}>
                            <img className={pageStyles.img} src={liberica} alt={"Debian Logo"}/>
                        </div>

                        <div className={pageStyles.verticalLine}></div>

                        <div className={pageStyles.logoHeadingWrapper}>
                            <img className={pageStyles.img} src={nik} alt={"Debian Logo"}/>
                        </div>
                    </div>
                </div>
            </BlockHeading>

            <BlockHeading text={"How to pull the images"}
                          headingClassName={"color-white"} className={cls("bg-dark", pageStyles.codeBlock)}>
                <CodeSnippet
                    code={`
<p class="p-1 color-white">Download the image with docker pull from Docker Hub, GHCR, Azure or GCR:</p>
<pre class="language-markup" data-disable-code-copy="true" tabindex="0"><code class="language-bash"><span class="token function">docker</span> pull <span class="token operator">&lt;</span>image name<span class="token operator">&gt;</span></code></pre>

<p class="p-1 color-white">Where the image name can be one of the following:</p>
<p class="p-1 color-white">Dockerhub:</p>
                    <pre class="language-markup" data-disable-code-copy="true" tabindex="0"><code class="language-markup">
docker.io/bellsoft/<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>repository</span><span class="token punctuation">&gt;</span></span>:<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>image_tag</span><span class="token punctuation">&gt;</span></span>
bellsoft/<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>repository</span><span class="token punctuation">&gt;</span></span>:<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>image_tag</span><span class="token punctuation">&gt;</span></span> (default is docker.io)</code></pre>

<p class="p-1 color-white">Github Container Registry:</p>
<pre class="language-markup" data-disable-code-copy="true" tabindex="0"><code class="language-markup">ghcr.io/bell-sw/<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>repository</span><span class="token punctuation">&gt;</span></span>:<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>image_tag</span><span class="token punctuation">&gt;</span></span></code></pre>
<p class="p-1 color-white">Microsoft Container Registry:</p>
<pre class="language-markup" data-disable-code-copy="true" tabindex="0"><code class="language-markup">bellsoft.azurecr.io/<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>repository</span><span class="token punctuation">&gt;</span></span>:<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>image_tag</span><span class="token punctuation">&gt;</span></span></code></pre>
<p class="p-1 color-white">Google Container Registry:</p>
<pre class="language-markup" data-disable-code-copy="true" tabindex="0"><code class="language-markup">gcr.io/<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>repository</span><span class="token punctuation">&gt;</span></span>:<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>image_tag</span><span class="token punctuation">&gt;</span></span></code></pre>
<p class="p-1 color-white">Amazon Elastic Container Registry:</p>
<pre class="language-markup" data-disable-code-copy="true" tabindex="0"><code class="language-markup">public.ecr.aws/bellsoft/<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>repository</span><span class="token punctuation">&gt;</span></span>:<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>image_tag</span><span class="token punctuation">&gt;</span></span></code></pre>
                  
<p class="p-1 color-white">Note that if the <code><span class="token tag">:<span class="token tag"><span class="token punctuation">&lt;</span>image-tag</span><span class="token punctuation">&gt;</span></span></code> part is omitted, it is substituted with the <code><span class="token tag">:<span class="token tag">latest</span></span></code>.</p>
<p class="p-1 color-white">Start a container from a pulled image with docker run:</p>
<pre class="language-bash" data-disable-code-copy="true" tabindex="0"><code class="language-bash"><span class="token function">docker</span> run -it --rm <span class="token operator">&lt;</span>image name<span class="token operator">&gt;</span></code></pre>

`}

                />
            </BlockHeading>

            <BlockHeading text={"Use the scheme to choose a suitable Alpaquita container image for your project"}
                          headingClassName={"color-white"} className={"bg-dark"}>
                <div className={"bs-flex-container l"}>
                    <img src={schema}/>
                </div>
            </BlockHeading>

            <a id={"docker-hub-images"}></a>
            <BlockHeading text={"Docker Hub images"} className={"bg-white"} headingAlign={"left"}>
                <div className={"bs-flex-container l"}>
                    <p className={"p-1 color-gray"}>
                        <a className={"link"} href={"https://hub.docker.com/u/bellsoft"}>BellSoft’s images</a> of
                        Alpaquita Containers are grouped into several repositories on Docker Hub, pertaining to a
                        specific language. Each repository contains a number of tags pointing to software version or
                        type.
                    </p>
                </div>
                <BlockHeading text={"Summary of Alpaquita Linux Docker Hub repositories"} className={"bg-white"}
                              headingAlign={"left"}>
                    <div className={"bs-flex-container l"}>
                        <div className={cls("blog-content")}>

                            <table className={pageStyles.faqTable}>
                                <thead>
                                <tr>
                                    <td className={"h-6"}>
                                            <span className={"h-6"}>
                                            Repo name
                                            </span>
                                    </td>
                                    <td className={"h-6"}>
                                            <span className={"h-6"}>
                                            Description
                                            </span>
                                    </td>
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                   href="https://hub.docker.com/r/bellsoft/liberica-runtime-container"
                                            >bellsoft/liberica-runtime-container</a></li>
                                        </ul>
                                    </td>
                                    <td>
                                        <PromoDescription color={""}>
                                            The repository contains images
                                            with <SmartLink to={"/libericajdk/"}>Liberica JDK Lite</SmartLink> and <SmartLink
                                            to={"/alpaquita-linux/"}>Alpaquita Linux</SmartLink>
                                            , <SmartLink to={"/alpaquita-linux/#crac-support"}>including versions with CRaC
                                            support</SmartLink>
                                        </PromoDescription>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                   href="https://hub.docker.com/r/bellsoft/alpaquita-linux-python"
                                            >bellsoft/alpaquita-linux-python</a></li>

                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains Alpaquita Linux images with Python and basic Python
                                        utilities
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                   href="https://hub.docker.com/r/bellsoft/alpaquita-linux-go"
                                            >bellsoft/alpaquita-linux-go</a></li>

                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains Alpaquita Linux images for building Go applications
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                   href="https://hub.docker.com/r/bellsoft/alpaquita-linux-gcc"
                                            >bellsoft/alpaquita-linux-gcc</a></li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains Alpaquita Linux images with the GCC compiler, tools, and
                                        libraries for development in C/C++
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                   href="https://hub.docker.com/r/bellsoft/liberica-native-image-kit-container"
                                            >bellsoft/liberica-native-image-kit-container</a></li>
                                        </ul>
                                    </td>
                                    <td>
                                        <PromoDescription color={""}>
                                            The repository contains Alpaquita Linux images with
                                            the <SmartLink to={"/liberica-native-image-kit/"}>Liberica Native Image
                                            Kit</SmartLink>
                                        </PromoDescription>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                   href="https://hub.docker.com/r/bellsoft/alpaquita-linux-base"
                                            >bellsoft/alpaquita-linux-base</a></li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The base images from this repository can be used 'as is' or serve as a base for
                                        customization by installing additional software from Alpaquita Linux
                                        repositories
                                    </td>
                                </tr>
                                </tbody>
                            </table>

                            <div className="accordion" id={accordionId1}>
                                <div className="accordion-item">
                                    <IdHeading headingLevel={3} className="accordion-header"
                                               id={accordionId1 + "-heading"}>
                                        <button className="accordion-button collapsed" type="button"
                                                data-bs-toggle="collapse"
                                                data-bs-target={"#" + accordionId1 + "-collapse"} aria-expanded="true"
                                                aria-controls={accordionId1 + "-collapse"}>
                                            <span className={"p-1 color-dark"}>The list of the latest Alpaquita Linux tags on Docker Hub.</span>
                                        </button>
                                    </IdHeading>
                                    <div id={accordionId1 + "-collapse"} className="accordion-collapse collapse"
                                         aria-labelledby={accordionId1 + "-heading"}>
                                        <ul className={"list-dotted"}>
                                            <li>latest</li>
                                            <li>musl</li>
                                            <li>glibc</li>
                                        </ul>
                                        <p>
                                            For liberica-runtime-container these tags could prove to be useful:
                                        </p>
                                        <ul className={"list-dotted"}>
                                            <li>jdk</li>
                                            <li>jre</li>
                                            <li>slim</li>
                                            <li>all</li>
                                            <li>crac</li>
                                        </ul>
                                        <p>
                                            You can also use tags to search for a specific Liberica JDK or Liberica NIK
                                            versions in liberica-runtime-container and
                                            liberica-native-image-kit-container
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </BlockHeading>
                <FAQ items={DhData.items} className={cls("bg-white")} faqClassName={pageStyles.faq}
                     title={"How to use Docker images with Alpaquita Linux"}
                     scrollable={false}/>
            </BlockHeading>

            <a id={"ghcr-images"}></a>
            <BlockHeading text={"GitHub Container Registry (GHCR) images"} headingAlign={"left"}>
                <div className={"bs-flex-container l"}>
                    <p className={"p-1 color-gray"}>
                        <a className={"link"} href={"https://github.com/bell-sw?tab=packages&repo_name=Alpaquita"}>BellSoft’s
                            GitHub public docker images</a> BellSoft’s GitHub public docker images of Alpaquita Linux
                        can be easily utilized in your projects. With GitHub CR, you can access the images anonymously.
                    </p>
                    <p className={"p-1 color-gray"}>
                        GHCR includes Alpaquita images with Liberica JDK Lite, Liberica NIK, C/C++, Python, Go, and the
                        base Alpaquita Linux image. All of them are provided with either optimized musl or glibc.
                    </p>
                </div>
                <BlockHeading text={"Summary of Alpaquita Linux GHCR repositories"} className={"bg-light-gray"}
                              headingAlign={"left"}>
                    <div className={"bs-flex-container l"}>
                        <div className={cls("blog-content")}>

                            <table className={pageStyles.faqTable}>
                                <thead>
                                <tr>
                                    <td className={"h-6"}>
                                            <span className={"h-6"}>
                                            Repo name
                                            </span>
                                    </td>
                                    <td className={"h-6"}>
                                            <span className={"h-6"}>
                                            Description
                                            </span>
                                    </td>
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                   href="https://github.com/bell-sw/Alpaquita/pkgs/container/liberica-runtime-container"
                                            >bell-sw/liberica-runtime-container</a></li>
                                        </ul>
                                    </td>
                                    <td>
                                        <PromoDescription color={""}>
                                            The repository contains images
                                            with <SmartLink to={"/libericajdk/"}>Liberica JDK Lite</SmartLink> and <SmartLink
                                            to={"/alpaquita-linux/"}>Alpaquita Linux</SmartLink>
                                            , <SmartLink to={"/alpaquita-linux/#crac-support"}>including versions with CRaC
                                            support</SmartLink>
                                        </PromoDescription>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                   href="https://github.com/bell-sw/Alpaquita/pkgs/container/alpaquita-linux-python"
                                            >bell-sw/alpaquita-linux-python</a></li>

                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains Alpaquita Linux images with Python and basic Python
                                        utilities
                                    </td>
                                </tr>

                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                   href="https://github.com/bell-sw/Alpaquita/pkgs/container/alpaquita-linux-go"
                                            >bell-sw/alpaquita-linux-go</a></li>

                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains Alpaquita Linux images for building Go applications
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                   href="https://github.com/bell-sw/Alpaquita/pkgs/container/alpaquita-linux-gcc"
                                            >bell-sw/alpaquita-linux-gcc</a></li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains Alpaquita Linux images with the GCC compiler, tools, and
                                        libraries for development in C/C++
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                   href="https://github.com/bell-sw/Alpaquita/pkgs/container/liberica-native-image-kit-container"
                                            >bell-sw/liberica-native-image-kit-container</a></li>
                                        </ul>
                                    </td>
                                    <td>
                                        <PromoDescription color={""}>
                                            The repository contains Alpaquita Linux images with
                                            the <SmartLink to={"/liberica-native-image-kit/"}>Liberica Native Image
                                            Kit</SmartLink>
                                        </PromoDescription>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                   href="https://github.com/bell-sw/Alpaquita/pkgs/container/alpaquita-linux-base"
                                            >bell-sw/alpaquita-linux-base</a></li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The base images from this repository can be used 'as is' or serve as a base for
                                        customization by installing additional software from Alpaquita Linux
                                        repositories
                                    </td>
                                </tr>
                                </tbody>
                            </table>

                            <div className="accordion" id={accordionId2}>
                                <div className="accordion-item">
                                    <IdHeading headingLevel={3} className="accordion-header"
                                               id={accordionId2 + "-heading"}>
                                        <button className="accordion-button collapsed" type="button"
                                                data-bs-toggle="collapse"
                                                data-bs-target={"#" + accordionId2 + "-collapse"} aria-expanded="true"
                                                aria-controls={accordionId2 + "-collapse"}>
                                            <span className={"p-1 color-dark"}>The list of the latest Alpaquita Linux tags on GHCR.</span>
                                        </button>
                                    </IdHeading>
                                    <div id={accordionId2 + "-collapse"} className="accordion-collapse collapse"
                                         aria-labelledby={accordionId2 + "-heading"}>
                                        <ul className={"list-dotted"}>
                                            <li>latest</li>
                                            <li>musl</li>
                                            <li>glibc</li>
                                        </ul>
                                        <p>
                                            For liberica-runtime-container these tags could prove to be useful:
                                        </p>
                                        <ul className={"list-dotted"}>
                                            <li>jdk</li>
                                            <li>jre</li>
                                            <li>slim</li>
                                            <li>all</li>
                                            <li>crac</li>
                                        </ul>
                                        <p>
                                            You can also use tags to search for a specific Liberica JDK or Liberica NIK
                                            versions in liberica-runtime-container and
                                            liberica-native-image-kit-container
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </BlockHeading>
                <FAQ items={GhcrData.items} title={"How to use GHCR images with Alpaquita Linux"}
                     faqClassName={pageStyles.faq} scrollable={false}/>
            </BlockHeading>

            <a id={"gcm-images"}></a>
            <BlockHeading text={"Google Cloud Marketplace (GCM) images"} className="bg-white" headingAlign={"left"}>
                <div className={"bs-flex-container l"}>
                    <p className={"p-1 color-gray"}> 
                        Google Cloud Marketplace includes Liberica Runtime Container with Liberica Lite and Alpaquita Linux,
                        Hardened Liberica Runtime Container, Liberica Native Image Kit Container 
                        with GraalVM CE-based Liberica Native Image Kit and Alpaquita Linux, 
                        Hardened Liberica Native image Kit Container, Alpaquita Linux Stream Container, 
                        and Alpaquita Hardened Base Image. All repositories include 
                        image variants with optimized musl or glibc
                    </p>
                </div>
                <BlockHeading text={"Summary of Alpaquita Linux GCM repositories" } className="bg-white" headingAlign={"left"}>
                    <div className={"bs-flex-container l"}>
                        <div className={cls("blog-content")}>

                            <table className={pageStyles.faqTable}>
                                <thead className={pageStyles.thead}>
                                <tr>
                                    <td className={"h-6"}>
                                            <span className={"h-6"}>
                                            Repo name
                                            </span>
                                    </td>
                                    <td className={"h-6"}>
                                            <span className={"h-6"}>
                                            Description
                                            </span>
                                    </td>
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                    href="https://marketplace.gcr.io/bellsoft-public/liberica-runtime-container"
                                            >bellsoft-public/liberica-runtime-container</a></li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains images with Liberica Lite and Alpaquita Linux
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                    href="https://marketplace.gcr.io/bellsoft-public/hardened-liberica-runtime-container"
                                            >bellsoft-public/hardened-liberica-runtime-container</a></li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains hardened images with Liberica Lite and Alpaquita Linux
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                    href="https://marketplace.gcr.io/bellsoft-public/alpaquita-base"
                                            >bellsoft-public/alpaquita-base</a></li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains images with Alpaquita Linux
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                    href="https://marketplace.gcr.io/bellsoft-public/hardened-base"
                                            >bellsoft-public/hardened-base</a></li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains hardened images with Alpaquita Linux
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                    href="https://marketplace.gcr.io/bellsoft-public/liberica-native-image-kit-container"
                                            >bellsoft-public/liberica-native-image-kit-container</a></li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains images with Liberica Native Image Kit and Alpaquita Linux
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li><a className="p-2 link"
                                                    href="https://marketplace.gcr.io/bellsoft-public/hardened-liberica-native-image-kit-container"
                                            >bellsoft-public/hardened-liberica-native-image-kit-container</a></li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains hardened images with Liberica Native Image Kit and Alpaquita Linux
                                    </td>
                                </tr>
                                </tbody>
                            </table>

                            <div className="accordion" id={accordionId2}>
                                <div className="accordion-item">
                                    <IdHeading headingLevel={3} className="accordion-header"
                                                id={accordionId2 + "-heading"}>
                                        <button className="accordion-button collapsed" type="button"
                                                data-bs-toggle="collapse"
                                                data-bs-target={"#" + accordionId2 + "-collapse"} aria-expanded="true"
                                                aria-controls={accordionId2 + "-collapse"}>
                                            <span className={"p-1 color-dark"}>The list of the latest Alpaquita Linux tags on GCM.</span>
                                        </button>
                                    </IdHeading>
                                    <div id={accordionId2 + "-collapse"} className="accordion-collapse collapse"
                                         aria-labelledby={accordionId2 + "-heading"}>
                                        <ul className={"list-dotted"}>
                                            <li>latest</li>
                                            <li>musl</li>
                                            <li>glibc</li>
                                        </ul>
                                        <p>
                                            For liberica-runtime-container these tags could prove to be useful:
                                        </p>
                                        <ul className={"list-dotted"}>
                                            <li>jdk</li>
                                            <li>jre</li>
                                            <li>slim</li>
                                            <li>all</li>
                                            <li>crac</li>
                                        </ul>
                                        <p>
                                            You can also use tags to search for a specific Liberica JDK or Liberica NIK
                                            versions in liberica-runtime-container and
                                            liberica-native-image-kit-container
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </BlockHeading>
                <FAQ items={GcrData.items} title={"How to use GCM images with Alpaquita Linux"}
                        faqClassName={pageStyles.faq} className="bg-white" scrollable={false}/>
            </BlockHeading>

            <a id={"mcr-images"}></a>
            <BlockHeading text={"Microsoft Container Registry (MCR) images"}
                          headingAlign={"left"}>
                <div className={"bs-flex-container l"}>
                    <p className={"p-1 color-gray"}>
                        Microsoft Container Registry includes Alpaquita images with Liberica JDK Lite, Liberica NIK,
                        C/C++,
                        Python, Go, and the base Alpaquita Linux image. All of them are provided with either optimized
                        musl
                        or glibc.
                    </p>
                </div>

                <BlockHeading text={"Summary of Alpaquita Linux MCR repositories"}
                              headingAlign={"left"}>
                    <div className={"bs-flex-container l"}>
                        <div className={cls("blog-content")}>

                            <table className={pageStyles.faqTable}>
                                <thead>
                                <tr>
                                    <td className={"h-6"}>
                                            <span className={"h-6"}>
                                            Repo name
                                            </span>
                                    </td>
                                    <td className={"h-6"}>
                                            <span className={"h-6"}>
                                            Description
                                            </span>
                                    </td>
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                bellsoft.azurecr.io/liberica-runtime-container
                                            </li>
                                        </ul>
                                    </td>
                                    <td>
                                        <PromoDescription color={""}>
                                            The repository contains images
                                            with <SmartLink to={"/libericajdk/"}>Liberica JDK Lite</SmartLink> and <SmartLink
                                            to={"/alpaquita-linux/"}>Alpaquita Linux</SmartLink>
                                            , <SmartLink to={"/alpaquita-linux/#crac-support"}>including versions with CRaC
                                            support</SmartLink>
                                        </PromoDescription>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                bellsoft.azurecr.io/alpaquita-linux-python
                                            </li>

                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains Alpaquita Linux images with Python and basic Python
                                        utilities
                                    </td>
                                </tr>

                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                bellsoft.azurecr.io/alpaquita-linux-go
                                            </li>

                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains Alpaquita Linux images for building Go applications
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>bellsoft.azurecr.io/alpaquita-linux-gcc</li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains Alpaquita Linux images with the GCC compiler, tools, and
                                        libraries for development in C/C++
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                bellsoft.azurecr.io/liberica-native-image-kit-container
                                            </li>
                                        </ul>
                                    </td>
                                    <td>
                                        <PromoDescription color={""}>
                                            The repository contains Alpaquita Linux images with
                                            the <SmartLink to={"/liberica-native-image-kit/"}>Liberica Native Image
                                            Kit</SmartLink>
                                        </PromoDescription>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                bellsoft.azurecr.io/alpaquita-linux-base
                                            </li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The base images from this repository can be used 'as is' or serve as a base for
                                        customization by installing additional software from Alpaquita Linux
                                        repositories
                                    </td>
                                </tr>
                                </tbody>
                            </table>

                            <div className="accordion" id={accordionId3}>
                                <div className="accordion-item">
                                    <IdHeading headingLevel={3} className="accordion-header"
                                               id={accordionId3 + "-heading"}>
                                        <button className="accordion-button collapsed" type="button"
                                                data-bs-toggle="collapse"
                                                data-bs-target={"#" + accordionId3 + "-collapse"} aria-expanded="true"
                                                aria-controls={accordionId3 + "-collapse"}>
                                            <span className={"p-1 color-dark"}>The list of the latest Alpaquita Linux tags on MCR.</span>
                                        </button>
                                    </IdHeading>
                                    <div id={accordionId3 + "-collapse"} className="accordion-collapse collapse"
                                         aria-labelledby={accordionId3 + "-heading"}>
                                        <ul className={"list-dotted"}>
                                            <li>latest</li>
                                            <li>musl</li>
                                            <li>glibc</li>
                                        </ul>
                                        <p>
                                            For liberica-runtime-container these tags could prove to be useful:
                                        </p>
                                        <ul className={"list-dotted"}>
                                            <li>jdk</li>
                                            <li>jre</li>
                                            <li>slim</li>
                                            <li>all</li>
                                            <li>crac</li>
                                        </ul>
                                        <p>
                                            You can also use tags to search for a specific Liberica JDK or Liberica NIK
                                            versions in liberica-runtime-container and
                                            liberica-native-image-kit-container
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </BlockHeading>
                <FAQ items={McrData.items} title={"How to use MCR images with Alpaquita Linux"}
                     faqClassName={pageStyles.faq} scrollable={false}/>
            </BlockHeading>

            <a id={"gcr-images"}></a>
            <BlockHeading text={"Google Container Registry (GCR) images"} className={"bg-white"} headingAlign={"left"}>
                <div className={"bs-flex-container l"}>
                    <p className={"p-1 color-gray"}>
                        Images of
                        Bellsoft <a href={"https://console.cloud.google.com/artifacts/docker/bellsoft/us/gcr.io"}>
                        Alpaquita Containers</a> can be found on Google Container Registry, currently
                        called Google Artifact Registry. GCR includes Alpaquita images with Liberica JDK Lite,
                        Liberica NIK, C/C++, Python, Go, and the base Alpaquita Linux image. All of them are provided
                        with
                        either optimized musl or glibc.
                    </p>
                </div>

                <BlockHeading text={"Summary of Alpaquita Linux GCR repositories"} className={"bg-white"}
                              headingAlign={"left"}>
                    <div className={"bs-flex-container l"}>
                        <div className={cls("blog-content")}>

                            <table className={pageStyles.faqTable}>
                                <thead>
                                <tr>
                                    <td className={"h-6"}>
                                            <span className={"h-6"}>
                                            Repo name
                                            </span>
                                    </td>
                                    <td className={"h-6"}>
                                            <span className={"h-6"}>
                                            Description
                                            </span>
                                    </td>
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                <a href={"https://console.cloud.google.com/artifacts/docker/bellsoft/us/gcr.io/liberica-runtime-container"}>
                                                    gcr.io/bellsoft/liberica-runtime-container</a>
                                            </li>
                                        </ul>
                                    </td>
                                    <td>
                                        <PromoDescription color={""}>
                                            The repository contains images
                                            with <SmartLink to={"/libericajdk/"}>Liberica JDK Lite</SmartLink> and <SmartLink
                                            to={"/alpaquita-linux/"}>Alpaquita Linux</SmartLink>
                                            , <SmartLink to={"/alpaquita-linux/#crac-support"}>including versions with CRaC
                                            support</SmartLink>
                                        </PromoDescription>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                <a href={"https://console.cloud.google.com/artifacts/docker/bellsoft/us/gcr.io/alpaquita-linux-python"}>
                                                    gcr.io/bellsoft/alpaquita-linux-python</a>
                                            </li>

                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains Alpaquita Linux images with Python and basic Python
                                        utilities
                                    </td>
                                </tr>

                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                <a href={"https://console.cloud.google.com/artifacts/docker/bellsoft/us/gcr.io/alpaquita-linux-go"}>
                                                    gcr.io/bellsoft/alpaquita-linux-go</a>
                                            </li>

                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains Alpaquita Linux images for building Go applications
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                <a href={"https://console.cloud.google.com/artifacts/docker/bellsoft/us/gcr.io/alpaquita-linux-gcc"}>
                                                    gcr.io/bellsoft/alpaquita-linux-gcc</a>
                                            </li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains Alpaquita Linux images with the GCC compiler, tools, and
                                        libraries for development in C/C++
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                <a href={"https://console.cloud.google.com/artifacts/docker/bellsoft/us/gcr.io/liberica-native-image-kit-container"}>
                                                    gcr.io/bellsoft/liberica-native-image-kit-container</a>
                                            </li>
                                        </ul>
                                    </td>
                                    <td>
                                        <PromoDescription color={""}>
                                            The repository contains Alpaquita Linux images with
                                            the <SmartLink to={"/liberica-native-image-kit/"}>Liberica Native Image
                                            Kit</SmartLink>
                                        </PromoDescription>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                <a href={"https://console.cloud.google.com/artifacts/docker/bellsoft/us/gcr.io/alpaquita-linux-base"}>
                                                    gcr.io/bellsoft/alpaquita-linux-base</a>
                                            </li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The base images from this repository can be used 'as is' or serve as a base for
                                        customization by installing additional software from Alpaquita Linux
                                        repositories
                                    </td>
                                </tr>
                                </tbody>
                            </table>

                            <div className="accordion" id={accordionId3}>
                                <div className="accordion-item">
                                    <IdHeading headingLevel={3} className="accordion-header"
                                               id={accordionId3 + "-heading"}>
                                        <button className="accordion-button collapsed" type="button"
                                                data-bs-toggle="collapse"
                                                data-bs-target={"#" + accordionId3 + "-collapse"} aria-expanded="true"
                                                aria-controls={accordionId3 + "-collapse"}>
                                            <span className={"p-1 color-dark"}>The list of the latest Alpaquita Linux tags on GCR.</span>
                                        </button>
                                    </IdHeading>
                                    <div id={accordionId3 + "-collapse"} className="accordion-collapse collapse"
                                         aria-labelledby={accordionId3 + "-heading"}>
                                        <ul className={"list-dotted"}>
                                            <li>latest</li>
                                            <li>musl</li>
                                            <li>glibc</li>
                                        </ul>
                                        <p>
                                            For liberica-runtime-container these tags could prove to be useful:
                                        </p>
                                        <ul className={"list-dotted"}>
                                            <li>jdk</li>
                                            <li>jre</li>
                                            <li>slim</li>
                                            <li>all</li>
                                            <li>crac</li>
                                        </ul>
                                        <p>
                                            You can also use tags to search for a specific Liberica JDK or Liberica NIK
                                            versions in liberica-runtime-container and
                                            liberica-native-image-kit-container
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </BlockHeading>
                <FAQ items={GcrData.items} title={"How to use GCR images with Alpaquita Linux"} className={"bg-white"}
                     faqClassName={pageStyles.faq} scrollable={false}/>
            </BlockHeading>

            <a id={"ecr-images"}></a>
            <BlockHeading text={"Amazon Elastic Container Registry (Amazon ECR) images"} 
                          headingAlign={"left"}>
                <div className={"bs-flex-container l"}>
                    <p className={"p-1 color-gray"}>
                        <a href={"https://gallery.ecr.aws/bellsoft"}>Amazon Elactic Container Registry</a> includes
                        Alpaquita images with Liberica JDK Lite, Liberica NIK, C/C++, Python, Go, and the
                        base Alpaquita Linux image. All of them are provided with either optimized musl or glibc.
                    </p>
                </div>

                <BlockHeading text={"Summary of Alpaquita Linux Amazon ECR repositories"} 
                              headingAlign={"left"}>
                    <div className={"bs-flex-container l"}>
                        <div className={cls("blog-content")}>

                            <table className={pageStyles.faqTable}>
                                <thead>
                                <tr>
                                    <td className={"h-6"}>
                                            <span className={"h-6"}>
                                            Repo name
                                            </span>
                                    </td>
                                    <td className={"h-6"}>
                                            <span className={"h-6"}>
                                            Description
                                            </span>
                                    </td>
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                public.ecr.aws/bellsoft/liberica-runtime-container
                                            </li>
                                        </ul>
                                    </td>
                                    <td>
                                        <PromoDescription color={""}>
                                            The repository contains images
                                            with <SmartLink to={"/libericajdk/"}>Liberica JDK Lite</SmartLink> and <SmartLink
                                            to={"/alpaquita-linux/"}>Alpaquita Linux</SmartLink>
                                            , <SmartLink to={"/alpaquita-linux/#crac-support"}>including versions with CRaC
                                            support</SmartLink>
                                        </PromoDescription>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                public.ecr.aws/bellsoft/alpaquita-linux-python
                                            </li>

                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains Alpaquita Linux images with Python and basic Python
                                        utilities
                                    </td>
                                </tr>

                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                public.ecr.aws/bellsoft/alpaquita-linux-go
                                            </li>

                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains Alpaquita Linux images for building Go applications
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                public.ecr.aws/bellsoft/alpaquita-linux-gcc
                                            </li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The repository contains Alpaquita Linux images with the GCC compiler, tools, and
                                        libraries for development in C/C++
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                public.ecr.aws/bellsoft/liberica-native-image-kit-container
                                            </li>
                                        </ul>
                                    </td>
                                    <td>
                                        <PromoDescription color={""}>
                                            The repository contains Alpaquita Linux images with
                                            the <SmartLink to={"/liberica-native-image-kit/"}>Liberica Native Image
                                            Kit</SmartLink>
                                        </PromoDescription>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={"p-2"}>
                                        <ul>
                                            <li>
                                                public.ecr.aws/bellsoft/alpaquita-linux-base
                                            </li>
                                        </ul>
                                    </td>
                                    <td className={"p-2"}>
                                        The base images from this repository can be used 'as is' or serve as a base for
                                        customization by installing additional software from Alpaquita Linux
                                        repositories
                                    </td>
                                </tr>
                                </tbody>
                            </table>

                            <div className="accordion" id={accordionId3}>
                                <div className="accordion-item">
                                    <IdHeading headingLevel={3} className="accordion-header"
                                               id={accordionId3 + "-heading"}>
                                        <button className="accordion-button collapsed" type="button"
                                                data-bs-toggle="collapse"
                                                data-bs-target={"#" + accordionId3 + "-collapse"} aria-expanded="true"
                                                aria-controls={accordionId3 + "-collapse"}>
                                            <span className={"p-1 color-dark"}>The list of the latest Alpaquita Linux tags on Amazon ECR.</span>
                                        </button>
                                    </IdHeading>
                                    <div id={accordionId3 + "-collapse"} className="accordion-collapse collapse"
                                         aria-labelledby={accordionId3 + "-heading"}>
                                        <ul className={"list-dotted"}>
                                            <li>latest</li>
                                            <li>musl</li>
                                            <li>glibc</li>
                                        </ul>
                                        <p>
                                            For liberica-runtime-container these tags could prove to be useful:
                                        </p>
                                        <ul className={"list-dotted"}>
                                            <li>jdk</li>
                                            <li>jre</li>
                                            <li>slim</li>
                                            <li>all</li>
                                            <li>crac</li>
                                        </ul>
                                        <p>
                                            You can also use tags to search for a specific Liberica JDK or Liberica NIK
                                            versions in liberica-runtime-container and
                                            liberica-native-image-kit-container
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </BlockHeading>
                <FAQ items={EcrData.items} title={"How to use Amazon ECR images with Alpaquita Linux"}
                     faqClassName={pageStyles.faq} scrollable={false} stickyMenu={true}/>
            </BlockHeading>

            <BlockHeading text={"Available tags and JDK versions"} headingAlign={"left"} className={"bg-white"}
            >
                <div className={"bs-flex-container l"}>
                    <p className={"p-1 color-dark"}>
                        The table below summarizes the tags available in BellSoft’s repositories across all container
                        registries.
                        Note that Liberica Runtime Container (Liberica JDK Lite with Alpaquita Linux*) supports only LTS
                        Java versions ({
                        getLtsJdkVersionsString()}).
                        The ‘latest’ tag in all repositories points to the latest version of an image.
                    </p>
                </div>
                <table className={pageStyles.table}>
                    <thead>
                    <tr>
                        <th>Versions</th>
                        <th>Tags</th>
                        <th>Tag structure</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td>Liberica Runtime Container</td>
                        <td>
                            <ul className={"list-dotted"}>
                                <li>Java version ({getLtsJdkVersionsWithLatestString()})</li>
                                <li>JDK type (jdk, jdk-all with jlink, jre)</li>
                                <li>libc type (musl or glibc)</li>
                            </ul>
                        </td>
                        <td>
                            {"<JDK type>-<Java version>-[crac]-[cds]-[slim]-<libc type>.  " +
                                "The slim version (with 'slim' in its tag) is smaller, as it does not  " +
                                "ship the APK package manager and consists of a single layer. CDS version " +
                                "(with 'cds' in its tag) contains Class Data Sharing archives which helps " +
                                "launching JVM faster. CRaC version (with 'crac' in its tag) supports " +
                                "Coordinated Restore at Checkpoint  for faster startup."}
                        </td>
                    </tr>
                    <tr>
                        <td>Alpaquita with NIK Container</td>
                        <td>
                            <ul className={"list-dotted"}>
                                <li>Java version (11, 17, 22)</li>
                                <li>NIK version (22, 24)</li>
                                <li>libc type (musl or glibc)</li>
                            </ul>
                        </td>
                        <td>jdk-&lt;Java version&gt;-nik-&lt;NIK version&gt;-&lt;libc type&gt;</td>
                    </tr>
                    <tr>
                        <td>Alpaquita with Python container</td>
                        <td>
                            <ul className={"list-dotted"}>
                                <li>Python version (3.x)</li>
                                <li>libc type (musl or glibc)</li>
                            </ul>
                        </td>
                        <td>&lt;Python version&gt;-&lt;libc type&gt;</td>
                    </tr>
                    <tr>
                        <td>Alpaquita with Go container</td>
                        <td>
                            <ul className={"list-dotted"}>
                                <li>Go version</li>
                                <li>libc type (musl or glibc)</li>
                            </ul>
                        </td>
                        <td>&lt;Go version&gt;-&lt;libc type&gt;</td>
                    </tr>
                    <tr>
                        <td>Alpaquita with the GCC container</td>
                        <td>
                            <ul className={"list-dotted"}>
                                <li>GCC version (13.x)</li>
                                <li>libc type (musl or glibc)</li>
                            </ul>
                        </td>
                        <td>&lt;GCC version&gt;-&lt;libc type&gt;</td>
                    </tr>
                    <tr>
                        <td>Alpaquita base image</td>
                        <td>
                            <ul className={"list-dotted"}>
                                <li>libc type (musl or glibc)</li>
                            </ul>
                        </td>
                        <td>stream-&lt;libc type&gt;</td>
                    </tr>
                    </tbody>
                </table>
            </BlockHeading>

            <a id="contacts-form"></a>
            <BackgroundImage
                desktop={<StaticImage src={"../../images/pages/alpaquita/form-desktop.png"}
                                      alt={"test"}/>}
                tablet={<StaticImage src={"../../images/pages/alpaquita/form-tablet.png"}
                                     alt={"test"}/>}
                mobile={<StaticImage src={"../../images/pages/alpaquita/form-mobile.png"}
                                     alt={"test"}/>}
                alt={"Contact Form Background"}
            >
                <ContactsForm
                    details="Get guidance on migrating to Liberica JDK"
                    title="Speak with an expert to see which solution will serve your business the best"

                    successDetails="Thank you for your interest in our company"
                    successTitle="We will send the guidance on migrating Liberica JDK to the specified email"
                    form={formItems} classNames={{ input: {container: formStyles.alpaquitaForm}}}
                />
            </BackgroundImage>
        </Layout>
    )
}

export default IndexPage

export const query = graphql`
  query {
    promoMobileImage: file(absolutePath: {regex: "/.*images\\/pages\\/alpaquita\\/promo-mobile.png/"}) {
      childImageSharp {
        fluid(fit: COVER, pngQuality: 90, webpQuality: 90, quality: 90, maxWidth: 1280) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    promoTabletImage: file(
      absolutePath: {regex: "/.*images\\/pages\\/alpaquita\\/promo-tablet.png/"}
    ) {
      childImageSharp {
        fluid(fit: COVER, pngQuality: 90, webpQuality: 90, quality: 90, maxWidth: 1280) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    promoDesktopImage: file(
      absolutePath: {regex: "/.*images\\/pages\\/alpaquita\\/promo-desktop.png/"}
    ) {
      childImageSharp {
        fluid(fit: COVER, pngQuality: 90, webpQuality: 90, quality: 90, maxWidth: 1280) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    forms: allFormsJson(filter: {fields: {hashedIdSelector: {in: ["#popup-request-quote"]}}}) {
            nodes {
              fields {
                idSelector
                hashedIdSelector
              }
              formId
              details
              title
              successDescription
              successDetails
              successTitle
              items {
                name
                placeholder
                required
                hidden
                type
                gridColumn
              }
              controls {
                terms {
                  name
                }
                button {
                  caption
                }
              }
            }
          }
  }
`

export const Head: HeadFC = () =>
    <>
        <title>Alpaquita container images | BellSoft Java</title>
        <meta name="description"
              content="BellSoft provides ready Alpaquita containers on Docker Hub, GHCR, MCR, GCR and Amazon ECR."/>
    </>
